import React, { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { dynamicTableStore } from "../DynamicTable/DynamicTableStore";
import useDynamicTable from "features/DynamicTable/useDynamicTable";
import { useTranslation } from "react-i18next";
import { DynamicColumnOUT } from "api-client";
import { OBJECT_CATEGORIES_ICONS } from "features/DynamicTable/utils";
import clsx from "clsx";

export const COLUMNS_GROUPS = {
  infoColumns: "infoColumns",
  groupingColumns: "groupingColumns",
};

const useTableColumns = () => {
  const { headerCell, columnCell, objectCountCell } = useDynamicTable();
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";

  const informationalColumnsKeys =
    dynamicTableStore.filterStore.getcurrentGroupKeys(
      COLUMNS_GROUPS.infoColumns
    );

  const informationalColumns = () => {
    const filtered = informationalColumnsKeys
      .map((key) =>
        dynamicTableStore.dynamicColumns.find((col) => col.key_name === key)
      )
      .filter(Boolean) as DynamicColumnOUT[];

    const groups: { columns: DynamicColumnOUT[] }[] = [];
    filtered.forEach((col) => {
      const lastGroup = groups[groups.length - 1];
      if (
        lastGroup &&
        (lastGroup.columns[0].group_name || "") === (col.group_name || "")
      ) {
        lastGroup.columns.push(col);
      } else {
        groups.push({ columns: [col] });
      }
    });

    const lastColumn =
      dynamicTableStore.tableColumnsOrder[
        dynamicTableStore.tableColumnsOrder.length - 1
      ];

    return groups.map((group, groupIndex) => {
      const isLastGroup =
        groupIndex === groups.length - 1 &&
        group.columns.map((col) => col.key_name).includes(lastColumn);

      const firstColumn = group.columns[0];
      const groupName = isEng
        ? firstColumn.group_name
        : firstColumn.group_name_de;

      return {
        id: groupName || COLUMNS_GROUPS.infoColumns,
        header: () =>
          headerCell(
            groupName || t(`mappingEditor.${COLUMNS_GROUPS.infoColumns}`),
            "",
            false,
            isLastGroup
          ),
        columns: group.columns.map((col) => {
          const tableColumnsOrder = dynamicTableStore.tableColumnsOrder;
          const currentIndex = tableColumnsOrder.indexOf(col.key_name);
          const nextColumnKey = tableColumnsOrder[currentIndex + 1];
          const nextColumn = dynamicTableStore.dynamicColumns.find(
            (c) => c.key_name === nextColumnKey
          );

          const isLastInGroup =
            !nextColumn || nextColumn.group_name !== col.group_name;

          return columnCell(
            false,
            col,
            clsx({
              "!border-r-gray-300": isLastInGroup,
            }),
            !!col.mask_duplicates
          );
        }),
        headerClassName: "border-r !border-r-gray-300",
        headerIcon: OBJECT_CATEGORIES_ICONS[firstColumn.group_name],
      };
    }) as ColumnDef<{ [key: string]: string }>[];
  };

  const groupingColumns = (isLastGroup: boolean) => {
    const visibleKeys = dynamicTableStore.filterStore.validGroupByRules.map(
      (item) => item.column_key
    );

    const filteredCols = visibleKeys
      .map((key) =>
        dynamicTableStore.dynamicColumns.find((col) => col.key_name === key)
      )
      .filter(Boolean) as DynamicColumnOUT[];

    const columns = filteredCols.map((col) => {
      const tableColumnsOrder = dynamicTableStore.tableColumnsOrder;
      const currentIndex = tableColumnsOrder.indexOf(col.key_name);
      const nextColumnKey = tableColumnsOrder[currentIndex + 1];
      const isLastInGroup = !visibleKeys.includes(nextColumnKey);

      return columnCell(
        true,
        col,
        isLastInGroup ? "!border-r-gray-300" : undefined
      );
    });

    return {
      id: COLUMNS_GROUPS.groupingColumns,
      header: () =>
        headerCell(
          t(`mappingEditor.${COLUMNS_GROUPS.groupingColumns}`),
          "text-white",
          true,
          isLastGroup
        ),
      columns: [objectCountCell(), ...columns],
      headerClassName: "bg-gray-500 text-white border-r border-r-gray-300",
    };
  };

  return useMemo<ColumnDef<{ [key: string]: string }>[]>(
    () => [
      ...(dynamicTableStore.filterStore.hasGrouping()
        ? [groupingColumns(informationalColumnsKeys.length === 0)]
        : []),
      ...(informationalColumnsKeys.length ? informationalColumns() : []),
    ],
    [
      dynamicTableStore.dynamicColumns.length,
      dynamicTableStore.visibleColumnProperties,
      dynamicTableStore.filterStore.validGroupByRules.length,
      dynamicTableStore.tableColumnsOrder,
    ]
  );
};

export default useTableColumns;
